<template>
  <div
    class="flex relative rounded-2xl border border-white focus:border-primary border-b-[#f0f1ff] transition-all duration-300"
    :class="{ '!border-primary border-b-primary': isFocus }"
  >
    <div class="relative">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ fromLabel }}
      </span>
      <input
        v-model="fromValue"
        @input="handleFromInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="fromPlaceholder"
        :style="{ paddingLeft: fromPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-l-[15px] text-sm border border-b-2 border-r-0 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <div class="relative">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ toLabel }}
      </span>
      <input
        v-model="toValue"
        @input="handleToInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="toPlaceholder"
        :style="{ paddingLeft: toPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-r-[15px] text-sm border border-b-2 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <el-slider
      v-model="rangeValue"
      :range="isRange"
      class="absolute -bottom-[1px] px-3 runway-none"
      tooltip-class="ui-tooltip"
      :min="0"
      :max="100"
      :format-tooltip="(value) => positionToValue(value).toString()"
      @update:model-value="handleRangeChange"
    />
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';
  interface Props {
    min?: number;
    max?: number;
    fromLabel?: string;
    toLabel?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    fromPadding?: string;
    toPadding?: string;
    isRange?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 100,
    fromLabel: "От",
    toLabel: "До",
    fromPlaceholder: "0",
    toPlaceholder: "0",
    isRange: true,
  });

  const emit = defineEmits(["update:modelValue"]);

  const modelValue = defineModel<number[]>({ required: true });

  const fromValue = ref<number>(props.min);
  const toValue = ref<number>(props.max);
  const rangeValue = ref<number[]>([0, 100]);

  const isFocus = ref(false);

  const createProgressiveScale = (maxValue: number) => {
    // Определяем подходящий шаг округления в зависимости от максимального значения
    let roundedMax: number;
    let stepSize: number;
    
    if (maxValue <= 100) {
      stepSize = 10;
      roundedMax = Math.ceil(maxValue / 10) * 10;
    } else if (maxValue <= 1000) {
      stepSize = 100;
      roundedMax = Math.ceil(maxValue / 100) * 100;
    } else if (maxValue <= 10000) {
      stepSize = 1000;
      roundedMax = Math.ceil(maxValue / 1000) * 1000;
    } else if (maxValue <= 100000) {
      stepSize = 10000;
      roundedMax = Math.ceil(maxValue / 10000) * 10000;
    } else {
      stepSize = 100000;
      roundedMax = Math.ceil(maxValue / 100000) * 100000;
    }
    
    const scale: number[] = [];
    let currentValue = 0;
    
    // Создаем прогрессивную шкалу с круглыми числами
    const steps = [
      { count: 10, step: 100 },
      { count: 8, step: 500 },
      { count: 5, step: 1000 },
      { count: 5, step: 2000 },
      { count: 10, step: 5000 },
      { count: 10, step: 10000 },
      { count: 10, step: 20000 },
      { count: 22, step: 50000 },
    ];
    
    for (const stepConfig of steps) {
      for (let i = 0; i < stepConfig.count; i++) {
        currentValue += stepConfig.step;
        if (currentValue <= roundedMax) {
          scale.push(currentValue);
        }
      }
    }
    
    // Распределяем оставшиеся шаги равномерно
    const remainingValue = roundedMax - currentValue;
    if (remainingValue > 0) {
      const remainingSteps = 100 - scale.length; // Оставляем место для максимального значения
      if (remainingSteps > 0) {
        const stepValue = Math.round(remainingValue / remainingSteps);
        for (let i = 0; i < remainingSteps; i++) {
          currentValue += stepValue;
          if (currentValue < roundedMax) {
            scale.push(currentValue);
          }
        }
      }
    }
    
    // Добавляем максимальное значение если его еще нет
    if (scale.length === 0 || scale[scale.length - 1] !== roundedMax) {
      scale.push(roundedMax);
    }
    
    return scale;
  };

  const progressiveScale = computed(() => createProgressiveScale(props.max));

  const positionToValue = (position: number): number => {
    if (position <= 0) return props.min;
    if (position >= 100) return progressiveScale.value[progressiveScale.value.length - 1];

    const scaledPosition = position;

    if (scaledPosition <= 1) {
      return Math.round(props.min + (progressiveScale.value[0] - props.min) * scaledPosition);
    }

    const index = Math.floor(scaledPosition) - 1;

    if (index >= progressiveScale.value.length - 1) {
      return progressiveScale.value[progressiveScale.value.length - 1];
    }

    const fraction = scaledPosition - Math.floor(scaledPosition);
    const currentVal = progressiveScale.value[index];
    const nextVal = progressiveScale.value[index + 1] || progressiveScale.value[progressiveScale.value.length - 1];

    return Math.round(currentVal + (nextVal - currentVal) * fraction);
  };

  const valueToPosition = (value: number): number => {
    const roundedMax = progressiveScale.value[progressiveScale.value.length - 1];
    
    if (value <= props.min) return 0;
    if (value >= roundedMax) return 100;

    if (value < progressiveScale.value[0]) {
      const ratio = (value - props.min) / (progressiveScale.value[0] - props.min);
      return ratio;
    }

    for (let i = 0; i < progressiveScale.value.length - 1; i++) {
      if (value <= progressiveScale.value[i]) {
        return i + 1;
      }
    }

    if (value < roundedMax) {
      const lastScaleValue = progressiveScale.value[progressiveScale.value.length - 1];
      const ratio = (value - lastScaleValue) / (roundedMax - lastScaleValue);
      return progressiveScale.value.length + ratio;
    }

    return 100;
  };

  const handleFromInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    fromValue.value = Math.min(Math.max(numValue, props.min), toValue.value);

    rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(toValue.value)];
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleToInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    const roundedMax = progressiveScale.value[progressiveScale.value.length - 1];
    toValue.value = Math.min(Math.max(numValue, fromValue.value), roundedMax);

    rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(toValue.value)];
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleRangeChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      fromValue.value = positionToValue(value[0]);
      toValue.value = positionToValue(value[1]);
      rangeValue.value = value;
      emit("update:modelValue", [fromValue.value, toValue.value]);
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "Home",
      "End",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    if (e.ctrlKey || e.metaKey) {
      const allowedCtrlKeys = ["a", "c", "v", "x", "z"];
      if (allowedCtrlKeys.includes(e.key.toLowerCase())) {
        return;
      }
    }

    if (/^[0-9]$/.test(e.key)) {
      return;
    }

    if (allowedKeys.includes(e.key)) {
      return;
    }

    e.preventDefault();
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData?.getData("text") || "";
    const numbersOnly = paste.replace(/\D/g, "");

    if (numbersOnly) {
      const target = e.target as HTMLInputElement;
      const currentValue = target.value;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      const newValue =
        currentValue.substring(0, selectionStart) +
        numbersOnly +
        currentValue.substring(selectionEnd);

      target.value = newValue;
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  watch(
    () => modelValue.value,
    (newValue) => {
      if (newValue && newValue.length === 2) {
        fromValue.value = newValue[0];
        toValue.value = newValue[1];
        rangeValue.value = [valueToPosition(newValue[0]), valueToPosition(newValue[1])];
      }
    },
    { immediate: true }
  );

  // Инициализируем значения при создании компонента
  onMounted(() => {
    if (progressiveScale.value.length > 0) {
      const roundedMax = progressiveScale.value[progressiveScale.value.length - 1];
      fromValue.value = props.min;
      toValue.value = roundedMax;
      rangeValue.value = [0, 100];
      emit("update:modelValue", [props.min, roundedMax]);
    }
  });

  // Следим за изменениями progressiveScale и обновляем значения
  watch(
    () => progressiveScale.value,
    (newScale) => {
      if (newScale.length > 0 && toValue.value === props.max) {
        const roundedMax = newScale[newScale.length - 1];
        toValue.value = roundedMax;
        rangeValue.value = [valueToPosition(fromValue.value), valueToPosition(roundedMax)];
        emit("update:modelValue", [fromValue.value, roundedMax]);
      }
    },
    { immediate: true }
  );
</script>

<style lang="scss" scoped>
  input {
    color: theme("colors.txmain");
    font-size: 14px;
    height: auto;
    background-color: theme("colors.bgmain");
    border: 0 !important;

    &:focus {
      --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width)
        var(--tw-ring-offset-color);
      --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width))
        var(--tw-ring-color);
      box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      --tw-ring-color: theme("colors.brfocus");
    }

    &::placeholder {
      color: theme("colors.txopacity");
    }
  }

  :deep(.el-slider) {
    .el-slider__runway {
      height: 2px;
      background-color: #f0f1ff;
    }

    .el-slider__bar {
      background-color: theme("colors.primary");
    }

    .el-slider__button {
      width: 14px;
      height: 14px;
      border: 2px solid white;
      background-color: theme("colors.primary");
      filter: drop-shadow(0px 1px 2.3px rgba(0, 0, 0, 0.12));
    }
  }
</style>

<style lang="scss">
  .ui-tooltip {
    background-color: white !important;
    color: theme("colors.txmain") !important;
    border: 0 !important;
    border-radius: 6px !important;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.12) !important;

    .el-popper__arrow {
      display: none;
    }
  }
</style>
